import {
    createDeserializer,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
} from "@jsonapi-serde/client";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod/mini";
import { zj } from "zod-joda/mini";
import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";

const localZonedDateTime = () =>
    z.pipe(
        zj.zonedDateTime(),
        z.transform((value) => value.withZoneSameInstant(localZoneId)),
    );

export const reviewRequestAttributesSchema = z.object({
    createdAt: localZonedDateTime(),
    firstOpenedAt: z.nullable(localZonedDateTime()),
    reviewSubmittedAt: z.nullable(localZonedDateTime()),
    fullName: z.string(),
    emailAddress: z.string(),
});

const deserializeRequest = createDeserializer({
    type: "review_request",
    cardinality: "one",
    attributesSchema: reviewRequestAttributesSchema,
});

const deserializeRequests = createDeserializer({
    type: "review_request",
    cardinality: "many",
    attributesSchema: reviewRequestAttributesSchema,
});

export type ReviewRequest = ReturnType<typeof deserializeRequest>["data"];
export type PaginatedReviewRequests = ReturnType<typeof deserializeRequests>;

export type ListReviewRequestsOptions = {
    pageParams?: PageParams;
};

export const createReviewRequestQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListReviewRequestsOptions) =>
        queryOptions({
            queryKey: ["review-requests", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/review-requests");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return deserializeRequests(await response.json());
            },
        }),
    get: (requestId: string) =>
        queryOptions({
            queryKey: ["review-request", requestId],
            queryFn: async ({ signal }) => {
                const response = await fetch(apiUrl(`/review-requests/${requestId}`), { signal });
                await handleJsonApiError(response);
                return deserializeRequest(await response.json()).data;
            },
        }),
});
